import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { MailX, MailCheck, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/common/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const email = searchParams.get('e') || '';
  const token = searchParams.get('t') || '';
  const [status, setStatus] = useState('working'); // working | done | resubscribed | error

  useEffect(() => {
    if (!email || !token) {
      setStatus('error');
      return;
    }
    fetch(`${API_URL}/api/marketing/unsubscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, token })
    })
      .then(res => res.ok ? setStatus('done') : setStatus('error'))
      .catch(() => setStatus('error'));
  }, [email, token]);

  const resubscribe = async () => {
    setStatus('working');
    try {
      const res = await fetch(`${API_URL}/api/marketing/resubscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
      setStatus(res.ok ? 'resubscribed' : 'error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
      <SEO title="Email Preferences — Minted" noindex />
      <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 max-w-md w-full text-center" data-testid="unsubscribe-card">
        {status === 'working' && (
          <>
            <Loader2 className="w-10 h-10 text-minted-600 mx-auto mb-4 animate-spin" />
            <p className="text-stone-600">Updating your email preferences…</p>
          </>
        )}
        {status === 'done' && (
          <>
            <MailX className="w-10 h-10 text-minted-700 mx-auto mb-4" />
            <h1 className="font-display text-xl text-stone-900 mb-2">You're unsubscribed</h1>
            <p className="text-sm text-stone-500 mb-1">{email}</p>
            <p className="text-sm text-stone-600 mb-6">
              You won't receive marketing emails from Minted anymore. You'll still get essential emails about your orders, offers and account.
            </p>
            <Button variant="outline" onClick={resubscribe} data-testid="resubscribe-btn">
              I changed my mind — resubscribe
            </Button>
          </>
        )}
        {status === 'resubscribed' && (
          <>
            <MailCheck className="w-10 h-10 text-minted-700 mx-auto mb-4" />
            <h1 className="font-display text-xl text-stone-900 mb-2">Welcome back!</h1>
            <p className="text-sm text-stone-600 mb-6">You'll keep receiving Minted news and offers.</p>
            <Button className="bg-minted-700 hover:bg-minted-800" onClick={() => navigate('/')} data-testid="back-home-btn">
              Back to Minted
            </Button>
          </>
        )}
        {status === 'error' && (
          <>
            <MailX className="w-10 h-10 text-stone-400 mx-auto mb-4" />
            <h1 className="font-display text-xl text-stone-900 mb-2">That link didn't work</h1>
            <p className="text-sm text-stone-600 mb-6">
              The unsubscribe link looks invalid or expired. Please use the link from your most recent email, or contact us via Help and we'll sort it instantly.
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>Back to Minted</Button>
          </>
        )}
      </div>
    </div>
  );
}
